export type AccessSystem = "registry" | "legacy";

export const accessScopes = {
  paywall: "paywall",
  access_group: "access_group",
  other: "other",
} as const;
export type AccessScope = keyof typeof accessScopes;

export const contentSpecTypes = {
  Space: "Space",
  SpaceGroup: "SpaceGroup",
  AccessGroup: "AccessGroup",
} as const;
export type ContentSpecType = keyof typeof contentSpecTypes;

export type ContentType =
  | "SpaceMember"
  | "SpaceGroupMember"
  | "AccessGroupCommunityMember";

export interface ChangeEvent {
  access_spec_id: number;
  access_spec_type: string;
  actor_id: number;
  actor_type: string;
  id: number;
  type: string;
}

export interface AccessSpec {
  id: number;
  name: string;
  type: ContentSpecType;
}

interface AccessRegistryCommunityMemberStateEntrySpaceContentSpec extends AccessSpec {
  type: "Space";
  type_label: string;
}
interface AccessRegistryCommunityMemberStateEntrySpaceGroupContentSpec extends AccessSpec {
  type: "SpaceGroup";
}
interface AccessRegistryCommunityMemberStateEntryAccessGroupContentSpec extends AccessSpec {
  spaces_count: number;
  type: "AccessGroup";
}

export type AccessRegistryCommunityMemberStateEntryContentSpec =
  | AccessRegistryCommunityMemberStateEntrySpaceContentSpec
  | AccessRegistryCommunityMemberStateEntrySpaceGroupContentSpec
  | AccessRegistryCommunityMemberStateEntryAccessGroupContentSpec;

export interface AccessRegistryCommunityMemberStateEntryContent {
  access_scope: AccessScope;
  access_system: AccessSystem;
  created_at: string;
  grant_events: ChangeEvent[];
  id: number;
  type: ContentType;
}

interface AccessRegistryCommunityMemberBaseStateEntry {
  content?: AccessRegistryCommunityMemberStateEntryContent;
  content_spec: AccessRegistryCommunityMemberStateEntryContentSpec;
}

export interface AccessRegistryCommunityMemberSpaceStateEntry extends AccessRegistryCommunityMemberBaseStateEntry {
  content_spec: AccessRegistryCommunityMemberStateEntrySpaceContentSpec;
}

export interface AccessRegistryCommunityMemberSpaceGroupStateEntry extends AccessRegistryCommunityMemberBaseStateEntry {
  content_spec: AccessRegistryCommunityMemberStateEntrySpaceGroupContentSpec;
  spaces: Record<string, AccessRegistryCommunityMemberSpaceStateEntry>;
}

export interface AccessRegistryCommunityMemberAccessGroupStateEntry extends AccessRegistryCommunityMemberBaseStateEntry {
  additional_entry_points_count?: number;
  connected_count: number;
  content_spec: AccessRegistryCommunityMemberStateEntryAccessGroupContentSpec;
  entry_point: EntryPoint[] | null;
  primary_entry_point?: EntryPoint | null;
  removed_count: number;
  space_groups: Record<
    string,
    AccessRegistryCommunityMemberSpaceGroupStateEntry
  >;
}

export interface DisconnectedAccessGroupPair {
  access_group_id: number;
  access_group_name: string;
  actionable: boolean;
  archived: boolean;
  paywall_id: number;
  paywall_name: string;
  paywall_unavailable: boolean;
  pending: boolean;
  // Fields emitted by future slices — optional until the serializer adds them.
  purchase_status?: "active" | "completed";
  purchase_type?: "installments" | "one_time" | "subscription";
  reconnectable?: boolean;
}

export interface AccessRegistryCommunityMemberState {
  access_groups: Record<
    string,
    AccessRegistryCommunityMemberAccessGroupStateEntry
  >;
  content_specs_granted_through_access_groups: Array<
    | AccessRegistryCommunityMemberStateEntrySpaceGroupContentSpec
    | AccessRegistryCommunityMemberStateEntrySpaceContentSpec
  >;
  disconnected_access_groups: DisconnectedAccessGroupPair[];
  space_groups: Record<
    string,
    AccessRegistryCommunityMemberSpaceGroupStateEntry
  >;
}

interface BaseContentSpecAccessDetails {
  id: number;
  name: string;
  type: ContentSpecType;
}

export interface AccessGroupAccessDetails extends BaseContentSpecAccessDetails {
  name: string;
  spaces?: SpaceAccessDetails[];
  type: "AccessGroup";
}

export interface SpaceAccessDetails extends BaseContentSpecAccessDetails {
  access_groups: AccessGroupAccessDetails[];
  space_group: SpaceGroupAccessDetails;
}

export interface SpaceGroupAccessDetails extends BaseContentSpecAccessDetails {
  access_groups: AccessGroupAccessDetails[];
}

export type ContentSpecAccessDetails =
  | AccessGroupAccessDetails
  | SpaceGroupAccessDetails
  | SpaceAccessDetails;

export type EntryPointType =
  | "paywall"
  | "admin"
  | "admin_reconnect"
  | "onboarding"
  | "workflow"
  | "api"
  | "circle_ai"
  | "generic"
  | "unknown";

export interface EntryPoint {
  entry_point_label: string;
  entry_point_type: EntryPointType;
}

export interface AccessGroupTableRow {
  additional_entry_points_count: number;
  connected_count: number;
  entry_point: EntryPoint[] | null;
  id: number;
  name: string;
  primary_entry_point: EntryPoint | null;
  removed_count: number;
}

export interface AccessGroupDrawerSpaceGroup {
  id: number;
  name: string;
}

export interface AccessGroupDrawerSpace {
  id: number;
  name: string;
  space_type: string;
}

export interface AccessGroupDrawerGrantedSpace extends AccessGroupDrawerSpace {
  entry_point: EntryPoint | null;
  granted_at: string;
}

export interface AccessGroupDrawerRevokedSpace extends AccessGroupDrawerSpace {
  revoke_label: string;
  revoked_at: string;
}

export interface AccessGroupDrawerGrantedSpaceGroup {
  space_group: AccessGroupDrawerSpaceGroup;
  spaces: AccessGroupDrawerGrantedSpace[];
}

export interface AccessGroupDrawerRevokedSpaceGroup {
  space_group: AccessGroupDrawerSpaceGroup;
  spaces: AccessGroupDrawerRevokedSpace[];
}

export interface AccessGroupDrawerPayload {
  granted: AccessGroupDrawerGrantedSpaceGroup[];
  revoked: AccessGroupDrawerRevokedSpaceGroup[];
}

export interface AccessGroupTimelineEvent {
  id: number;
  kind: string;
  occurred_at: string;
  sentence: string;
}

export interface AccessGroupTimelineCard {
  entry_point: {
    label: string | null;
    type: string | null;
  } | null;
  grant_event: AccessGroupTimelineEvent | null;
  id: number | null;
  paywall_id: number | null;
  pending: boolean;
  purchase_status: "active" | "completed" | null;
  purchase_type: "installments" | "one_time" | "subscription" | null;
  reconnectable: boolean;
  revoke_event: AccessGroupTimelineEvent | null;
  space_count: number | null;
  status: "active" | "revoked";
}

export interface AccessGroupTimelinePayload {
  cards: AccessGroupTimelineCard[];
}

export interface HistoryEntry {
  access_group_id: number;
  additional_entry_points_count: number;
  archived: boolean;
  disconnected: boolean;
  name: string;
  primary_entry_point: EntryPoint;
  removed_at: string;
  revoke_label: string;
}
